import { Navigate, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchCurrentUser } from "@/api/auth";
import { isLoggedIn } from "@/api/client";
import { AppShell } from "@/shell/AppShell";
import { DashboardPage } from "@/pages/DashboardPage";
import { BookingsPage } from "@/pages/BookingsPage";
import { BookingDetailPage } from "@/pages/BookingDetailPage";
import { CalendarPage } from "@/pages/CalendarPage";
import { NewBookingPage } from "@/pages/NewBookingPage";
import { InquiriesPage } from "@/pages/InquiriesPage";
import { PosteingangPage } from "@/pages/PosteingangPage";
import { LoginPage } from "@/pages/LoginPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { StatistikPage } from "@/pages/StatistikPage";
import { KonusFormPage } from "@/pages/KonusFormPage";
import { PublicInquiryPage } from "@/pages/PublicInquiryPage";
import { PublicCalendarPage } from "@/pages/PublicCalendarPage";

function RequireAuth({ children }: { children: React.ReactNode }) {
  if (!isLoggedIn()) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { isLoading, isError } = useQuery({
    queryKey: ["me"],
    queryFn: fetchCurrentUser,
    retry: false,
    enabled: !!isLoggedIn(),
  });

  if (isLoggedIn() && isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--bg)",
          color: "var(--fg-muted)",
          fontFamily: "var(--font-body)",
          fontSize: 14,
        }}
      >
        Lade…
      </div>
    );
  }

  if (isLoggedIn() && isError) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <AppShell>
              <DashboardPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/bookings"
        element={
          <RequireAuth>
            <AppShell>
              <BookingsPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/bookings/:id"
        element={
          <RequireAuth>
            <AppShell>
              <BookingDetailPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/calendar"
        element={
          <RequireAuth>
            <AppShell>
              <CalendarPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/bookings/new"
        element={
          <RequireAuth>
            <AppShell>
              <NewBookingPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/inquiries"
        element={
          <RequireAuth>
            <AppShell>
              <InquiriesPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/posteingang"
        element={
          <RequireAuth>
            <AppShell>
              <PosteingangPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <AppShell>
              <SettingsPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route
        path="/statistik"
        element={
          <RequireAuth>
            <AppShell>
              <StatistikPage />
            </AppShell>
          </RequireAuth>
        }
      />
      <Route path="/anfrage" element={<PublicInquiryPage />} />
      <Route path="/kalender" element={<PublicCalendarPage />} />
      <Route path="/kalender-pfadi" element={<PublicCalendarPage includeDG />} />
      <Route path="/konus/:token" element={<KonusFormPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return <AppRoutes />;
}
